import type { RentFrequencyType } from '@/lib/types/property';

const MONTHS_PER_YEAR = 12;
const WEEKS_PER_YEAR = 52;
const DAYS_PER_YEAR = 365;

export type MonthlyRentInputs = {
  netRentPerSf: number | null | undefined;
  additionalRentPerSf?: number | null | undefined;
  unitSqFt: number | null | undefined;
  rentFrequency?: RentFrequencyType | null | undefined;
};

export function normalizeMonthlyRentPerSf(
  value: number,
  rentFrequency: RentFrequencyType | null | undefined,
): number;
export function normalizeMonthlyRentPerSf(
  value: number | null | undefined,
  rentFrequency: RentFrequencyType | null | undefined,
): number | null;
export function normalizeMonthlyRentPerSf(
  value: number | null | undefined,
  rentFrequency: RentFrequencyType | null | undefined,
): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return value * resolveMonthlyFrequencyFactor(rentFrequency);
}

export function normalizeAnnualRentPerSf(
  value: number,
  rentFrequency: RentFrequencyType | null | undefined,
): number;
export function normalizeAnnualRentPerSf(
  value: number | null | undefined,
  rentFrequency: RentFrequencyType | null | undefined,
): number | null;
export function normalizeAnnualRentPerSf(
  value: number | null | undefined,
  rentFrequency: RentFrequencyType | null | undefined,
): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return value * resolveAnnualFrequencyFactor(rentFrequency);
}

export function calculateMonthlyRent(inputs: MonthlyRentInputs): number | null {
  const { netRentPerSf, unitSqFt, additionalRentPerSf, rentFrequency } = inputs;

  if (typeof netRentPerSf !== 'number' || !Number.isFinite(netRentPerSf) || netRentPerSf <= 0) {
    return null;
  }

  if (typeof unitSqFt !== 'number' || !Number.isFinite(unitSqFt) || unitSqFt <= 0) {
    return null;
  }

  const normalizedNetRentPerSf = normalizeMonthlyRentPerSf(netRentPerSf, rentFrequency);
  if (normalizedNetRentPerSf <= 0) {
    return null;
  }

  const normalizedAdditionalRentPerSf = normalizeMonthlyRentPerSf(
    typeof additionalRentPerSf === 'number' && Number.isFinite(additionalRentPerSf)
      ? additionalRentPerSf
      : 0,
    rentFrequency,
  );

  return (normalizedNetRentPerSf + normalizedAdditionalRentPerSf) * unitSqFt;
}

function resolveMonthlyFrequencyFactor(
  rentFrequency: RentFrequencyType | null | undefined,
): number {
  switch (rentFrequency) {
    case 'annually':
      return 1 / MONTHS_PER_YEAR;
    case 'quarterly':
      return 1 / 3;
    case 'weekly':
      return WEEKS_PER_YEAR / MONTHS_PER_YEAR;
    case 'daily':
      return DAYS_PER_YEAR / MONTHS_PER_YEAR;
    case 'monthly':
    default:
      return 1;
  }
}

function resolveAnnualFrequencyFactor(rentFrequency: RentFrequencyType | null | undefined): number {
  switch (rentFrequency) {
    case 'annually':
      return 1;
    case 'quarterly':
      return 4;
    case 'weekly':
      return WEEKS_PER_YEAR;
    case 'daily':
      return DAYS_PER_YEAR;
    case 'monthly':
    default:
      return MONTHS_PER_YEAR;
  }
}
